import { runScript } from '../../_helpers/runScript.js';
import { iso6391To6392B } from '../../_data/iso6391To6392B.js';

(function () {
  const VIDEO_HEIGHT = 421;

  const PLAY = 'play';
  const PAUSE = 'pause';
  const hideClass = 'hide';
  const POPOVERSHOW = 'popovershow';
  const CAPTIONSCHANGES = 'captionschange';
  const SlingTracker = window?.GA4?.analyticsGa4?.SlingTracker;

  let lang = document.documentElement.lang;

  // Convert ISO 639-1 code to ISO 639-2
  let threeLetterLang = iso6391To6392B[lang] || lang; // Default to the original if no mapping found

  runScript(_init);

  function _init() {
    window._wq = window._wq || [];
    _wq.push({
      id: '_all',
      onHasData: function (video) {
        let swiperContainer = video.container.closest('.swiper');

        if (!swiperContainer) return;

        video.height(VIDEO_HEIGHT, { constrain: true });
        _updateResolution(video);

        window.addEventListener('resize', () => _updateResolution(video));
        video.bind(PLAY, () => _handleInterruption(PLAY, swiperContainer));
        video.bind(PAUSE, () => _handleInterruption(PAUSE, swiperContainer));
      },
      onReady: function (video) {
        let selectedCaptionLanguage = '';

        const asBackground = video.container.closest('[data-as-background="true"]');
        if (asBackground) {
          _handleCustomVideoState(video);
          _removeBottomBar(video);
          video.bind(PLAY, () => _handleCustomVideoBackgroundButtons(video, PLAY));

          video.bind(PAUSE, () => _handleCustomVideoBackgroundButtons(video, PAUSE));

          _updatePosition(video);
        } else {
          if (video?.options?.popover) {
            video.bind(PLAY, () => updateVideoCaption(video));
          } else {
            updateVideoCaption(video, selectedCaptionLanguage);
          }

          video.bind(CAPTIONSCHANGES, (details) => {
            selectedCaptionLanguage = details.language;
          });
        }

        const inVideoLibrary = video.container.closest('[data-video-library="true"]');

        if (inVideoLibrary) {
          video.bind(POPOVERSHOW, () => _handleVideoLibraryGa4Tracker(video));
        }
      },
    });
  }

  function _updateResolution(video) {
    let wistaContainer = video.container.closest('.arjo-a-wistia-video');
    let videoContainer = video.container;

    if (!wistaContainer) {
      return;
    }

    let wistaContainerRect = wistaContainer.getBoundingClientRect();
    let videoContainerRect = videoContainer.getBoundingClientRect();

    if (wistaContainerRect.height / wistaContainerRect.width > videoContainerRect.height / videoContainerRect.width) {
      video.width(wistaContainerRect.width, { constrain: true });
      video.height('');
    } else {
      video.height(wistaContainerRect.height, { constrain: true });
      video.width('');
    }
  }

  const updateVideoCaption = (video, selectedCaptionLanguage) => {
    video.plugin('captions').then(function (data) {
      // Check if video doesn't have captions
      if (!data?.captions?.length) {
        video.plugin.captions.disable();
        return;
      }

      if (selectedCaptionLanguage) {
        video.plugin.captions.setLanguage(selectedCaptionLanguage);
        return;
      }

      const matchingLangCaption = data.captions.find((caption) => caption.language === threeLetterLang);
      // Check if any caption object matches the current site language
      if (matchingLangCaption) {
        video.plugin.captions.setLanguage(threeLetterLang);
        return;
      }

      const englishCaption = data.captions.find((caption) => caption.language === iso6391To6392B.en);
      // Otherwise, check if any caption object has english
      if (englishCaption) {
        video.plugin.captions.setLanguage(iso6391To6392B.en);
        return;
      }

      video.plugin.captions.turnOff();
    });
  };

  const _updatePosition = (video) => {
    let wistaContainer = video.container.closest('.arjo-a-wistia-video');
    const containerWidth = wistaContainer?.offsetWidth;
    const responsiveContainer = wistaContainer.querySelector('.wistia_responsive_padding');

    if (!responsiveContainer || !containerWidth) return;

    const computedStyle = window.getComputedStyle(responsiveContainer);
    const paddingTop = computedStyle.getPropertyValue('padding-top');
    const centerPercentage = 50;
    const paddingTopPercentage = (paddingTop.replace('px', '') / containerWidth) * 100;
    const backgroundTopPosition = `${centerPercentage - (paddingTopPercentage - centerPercentage)}%`;
    responsiveContainer.style.setProperty('--background-top-position', backgroundTopPosition);
  };

  function _removeBottomBar(video) {
    const container = video.container.querySelector('.w-bottom-bar');
    container.remove();
  }

  function _handleInterruption(type, swiperContainer) {
    const isPlay = type === PLAY;
    swiperContainer && (swiperContainer.swiper.allowTouchMove = !isPlay);
  }

  function _getCustomButtonContainer(video) {
    const videoWrapper = video.container.closest('.arjo-a-wistia-video');

    if (!videoWrapper) return;

    const buttonContainer = videoWrapper.querySelector('.arjo-a-wistia-video__background-buttons');

    if (!buttonContainer) return;

    return buttonContainer;
  }

  function _toggleVisibilityVideoBackgroundButton(element, state) {
    try {
      const playButton = element.querySelector('[data-button="play"]');
      const pauseButton = element.querySelector('[data-button="pause"]');

      switch (state) {
        case PAUSE:
          playButton.classList.add(hideClass);
          pauseButton.classList.remove(hideClass);
          break;
        case PLAY:
          playButton.classList.remove(hideClass);
          pauseButton.classList.add(hideClass);
          break;
        default:
          return;
      }
    } catch (error) {
      console.log(error);
    }
  }

  function _handleCustomVideoState(video) {
    const buttonContainer = _getCustomButtonContainer(video);

    if (!buttonContainer) return;

    buttonContainer.addEventListener('click', (event) => {
      event.preventDefault();
      const state = video.state();

      switch (state) {
        case 'paused':
          video.play();
          break;
        case 'playing':
          video.pause();
          break;
        default:
          return;
      }
    });
  }

  function _handleCustomVideoBackgroundButtons(video, state) {
    const buttonContainer = _getCustomButtonContainer(video);

    if (!buttonContainer) return;
    _toggleVisibilityVideoBackgroundButton(buttonContainer, state);
  }

  function _handleVideoLibraryGa4Tracker(video) {
    const videoId = video?._hashedId;

    const currentContentTypeTitle = video?.container?.closest?.('[data-current-list-title]')?.dataset?.currentContentTypeTitle || 'undefined';
    const trackerCollectionNames = video?.container?.closest?.('[data-tracker-collection-names]')?.dataset?.trackerCollectionNames || 'undefined';
    const trackerFilterOptions = video?.container?.closest?.('[data-tracker-filter-options]')?.dataset?.trackerFilterOptions || 'undefined';

    // TODO: Update later if needed
    // SlingTracker?.trackVideoClick(currentContentTypeTitle, videoId, trackerCollectionNames, trackerFilterOptions);
  }
})();
